/** Öffentliche Seite „Neu im Laden“ (E14.5) unter /neu/:slug: frische Artikel aus freigegebenen Regalen, Events, Newsletter-Anmeldung. */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Illustration, Notice, Seitenzustand, TextField } from "../ui";
import { Marke } from "../ui/Marke";
import { api, ApiFehler, rufe } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { RechtlichesFuss } from "./Rechtliches";

interface Neu {
  laden: { name: string; slug: string; telefon: string; adresse: { strasse?: string; plz?: string; ort?: string }; oeffnungszeiten: Record<string, { von: string; bis: string } | null>; logo: boolean; akzent: string | null; newsletter: boolean; turnstileSiteKey: string | null };
  artikel: { id: string; bezeichnung: string; preisCent: number; kategorie: string; groesse: string; foto: boolean; regal: string | null; seit: string; anzeigename: string }[];
  events: { id: string; name: string; text: string; von: string; bis: string; rabattProzent: number | null }[];
}

const TAGE: [string, string][] = [["mo", "Mo"], ["di", "Di"], ["mi", "Mi"], ["do", "Do"], ["fr", "Fr"], ["sa", "Sa"], ["so", "So"]];

export function NeuImLaden() {
  const { slug = "" } = useParams();
  const q = useQuery({ queryKey: ["public", "neu", slug], queryFn: () => rufe<Neu>(api.api.public.laden[":slug"].neu.$get({ param: { slug } })), retry: false });
  useEffect(() => {
    const akzent = q.data?.laden.akzent ?? null;
    if (akzent) document.documentElement.style.setProperty("--accent", akzent);
    return () => {
      document.documentElement.style.removeProperty("--accent");
    };
  }, [q.data?.laden.akzent]);
  return (
    <div className="app-public app-public--breit">
      <Seitenzustand query={q} lines={6}>
        {(d) => (
          <div className="app-public__card">
            <div className="app-public__brand">
              {d.laden.logo ? <img src={`/api/public/laden/${d.laden.slug}/logo`} alt="" className="app-logo" width={40} height={40} /> : <Marke size={40} />}
              <span className="sc-strong">{d.laden.name}</span>
            </div>
            <h1 className="title">Neu im Laden</h1>
            <p className="sc-muted">Frisch eingeräumt in den letzten 14 Tagen. Kaufen kannst du nur im Laden – komm vorbei.</p>
            {d.events.length ? (
              <div className="sc-col" style={{ gap: "var(--space-2)" }}>
                {d.events.map((ev) => (
                  <Notice key={ev.id} tone="info" icon="sparkles" title={`${ev.name} · ${ev.von === ev.bis ? formatTag(ev.von) : `${formatTag(ev.von)} – ${formatTag(ev.bis)}`}`}>
                    {ev.text}{ev.rabattProzent ? ` ${ev.rabattProzent} % auf alles.` : ""}
                  </Notice>
                ))}
              </div>
            ) : null}
            {d.artikel.length === 0 ? (
              <div className="sc-center"><Illustration name="empty-shelf" /><p className="sc-muted">Gerade nichts Neues – schau bald wieder vorbei.</p></div>
            ) : (
              <div className="app-neu-raster">
                {d.artikel.map((a) => (
                  <div key={a.id} className="app-neu-karte">
                    {a.foto ? <img src={`/api/public/laden/${d.laden.slug}/neu/${a.id}/foto`} alt="" loading="lazy" /> : <div className="app-neu-karte__leer"><Illustration name="empty-shelf" /></div>}
                    <div className="app-neu-karte__text">
                      <span className="sc-strong">{a.bezeichnung}</span>
                      <span className="sc-muted sc-small">{[a.kategorie, a.groesse].filter(Boolean).join(" · ")}{a.regal ? ` · Regal ${a.regal}` : ""}{a.anzeigename ? ` · von ${a.anzeigename}` : ""}</span>
                      <span className="sc-num sc-strong">{formatEuro(a.preisCent)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {d.laden.newsletter ? <NewsletterForm slug={d.laden.slug} siteKey={d.laden.turnstileSiteKey} /> : null}
            <div className="sc-muted sc-small">
              <div className="sc-strong">Öffnungszeiten</div>
              {TAGE.map(([k, t]) => <div key={k}>{t}: {d.laden.oeffnungszeiten[k] ? `${d.laden.oeffnungszeiten[k]!.von} – ${d.laden.oeffnungszeiten[k]!.bis}` : "geschlossen"}</div>)}
              {d.laden.adresse.strasse ? <div style={{ marginTop: "var(--space-2)" }}>{d.laden.adresse.strasse}, {d.laden.adresse.plz} {d.laden.adresse.ort}</div> : null}
              <div style={{ marginTop: "var(--space-2)" }}><Link to={`/w/${d.laden.slug}`}>Selbst ein Regal mieten – auf die Warteliste</Link></div>
            </div>
            <RechtlichesFuss />
          </div>
        )}
      </Seitenzustand>
    </div>
  );
}

function NewsletterForm({ slug, siteKey }: { slug: string; siteKey: string | null }) {
  const [form, setForm] = useState({ email: "", name: "" });
  const [token, setToken] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fertig, setFertig] = useState(false);
  const turnstile = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey || !turnstile.current) return;
    const w = window as unknown as { turnstile?: { render: (el: HTMLElement, o: { sitekey: string; callback: (t: string) => void; theme: string; language: string; size: string }) => void } };
    const rendern = () => w.turnstile?.render(turnstile.current!, { sitekey: siteKey, callback: setToken, theme: "auto", language: "de", size: "flexible" });
    if (w.turnstile) rendern();
    else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = rendern;
      document.head.appendChild(s);
    }
  }, [siteKey]);
  async function senden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!form.email.includes("@")) return setFehler("Bitte eine gültige E-Mail-Adresse eingeben.");
    setLaeuft(true);
    try {
      await rufe(api.api.public.laden[":slug"].newsletter.$post({ param: { slug }, json: { email: form.email.trim(), name: form.name.trim(), turnstile: token ?? undefined } }));
      setFertig(true);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally {
      setLaeuft(false);
    }
  }
  if (fertig) return <Notice tone="success" title="Fast geschafft">Wir haben dir eine E-Mail geschickt. Bestätige den Link darin, dann bekommst du Neuigkeiten aus dem Laden.</Notice>;
  return (
    <form className="app-form" onSubmit={(e) => void senden(e)} noValidate>
      <div className="sc-strong">Neuigkeiten per E-Mail</div>
      <p className="sc-muted sc-small">Nur Neues aus diesem Laden, jederzeit abbestellbar. Deine Adresse geht an niemanden sonst.</p>
      <TextField label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" inputMode="email" />
      <TextField label="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" maxLength={80} />
      {siteKey ? <div ref={turnstile} /> : null}
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Button type="submit" variant="secondary" icon="mail" loading={laeuft} disabled={!!siteKey && !token}>Eintragen</Button>
    </form>
  );
}

/** /n/:slug/bestaetigen/:token und /n/:slug/abmelden/:token */
export function NewsletterBestaetigung({ art }: { art: "bestaetigen" | "abmelden" }) {
  const { slug = "", token = "" } = useParams();
  const q = useQuery({
    queryKey: ["public", "newsletter", art, token],
    queryFn: () => (art === "bestaetigen" ? rufe<{ laden: { name: string; slug: string } }>(api.api.public.newsletter.bestaetigen[":token"].$post({ param: { token } })) : rufe<{ laden: { name: string; slug: string } }>(api.api.public.newsletter.abmelden[":token"].$post({ param: { token } }))),
    retry: false,
  });
  return (
    <div className="app-public">
      <div className="app-public__card sc-center" style={{ padding: 0 }}>
        <Seitenzustand query={q} lines={3}>
          {(d) => (
            <>
              <Illustration name="all-done" />
              <h1 className="display">{art === "bestaetigen" ? "Danke, du bist dabei" : "Abgemeldet"}</h1>
              <p>{art === "bestaetigen" ? `Du bekommst ab jetzt Neuigkeiten von ${d.laden.name}.` : `Du bekommst keine E-Mails mehr von ${d.laden.name}.`}</p>
              <Link to={`/neu/${d.laden.slug || slug}`} className="rg-btn rg-btn--secondary">Neu im Laden ansehen</Link>
            </>
          )}
        </Seitenzustand>
        <RechtlichesFuss />
      </div>
    </div>
  );
}
